import { useCallback, useEffect, SyntheticEvent } from 'react';

import type { MouseHandlerDataParam } from 'recharts';

import { useChartContext } from '../../../ChartProvider';

type UseChartZoomHandlersReturn = {
  onMouseDown(nextState: MouseHandlerDataParam, event: SyntheticEvent): void;
  onMouseMove(nextState: MouseHandlerDataParam, event: SyntheticEvent): void;
};

const isScatterShapeTarget = (target: EventTarget | null): boolean => {
  if (target === null) {
    return false;
  }
  return Boolean(
    (target as Element).closest('[data-testid*="-scatter-shape-"]')
  );
};

/**
 * Provides mouse event handlers for chart zoom interactions.
 *
 * Manages the zoom selection flow:
 * - onMouseDown: Initiates a zoom region when the user clicks on chart
 * - onMouseMove: Updates a zoom region as user drags
 * - mouseup (via useEffect): Finalizes zoom or cancels if not dragging
 *
 * Prevents text selection of axis labels during zoom interactions.
 * Skips zoom start on scatter shapes so shape onClick still fires
 * (mousedown preventDefault would cancel the click).
 *
 * @returns Object containing onMouseDown and onMouseMove handlers to be attached to the chart container
 */
export const useChartContainerZoomHandlers = (): UseChartZoomHandlersReturn => {
  const {
    data,
    zoomEnabled,
    refAreaLeft,
    setRefAreaLeft,
    refAreaRight,
    setRefAreaRight,
    zoom,
  } = useChartContext();

  useEffect(() => {
    // if zoom's not started - do nothing
    if (refAreaLeft === null) {
      return;
    }

    const handleMouseUp = (event: MouseEvent) => {
      if (zoomEnabled && event.isTrusted) {
        if (refAreaLeft !== null && refAreaRight !== null) {
          zoom();
        } else {
          // If the user is not dragging, we need to reset the ref areas
          setRefAreaLeft(null);
          setRefAreaRight(null);
        }
      }
    };

    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    refAreaLeft,
    refAreaRight,
    setRefAreaLeft,
    setRefAreaRight,
    zoom,
    zoomEnabled,
  ]);

  const onMouseDown = useCallback(
    (nextState: MouseHandlerDataParam, e: SyntheticEvent) => {
      if (isScatterShapeTarget(e.target)) {
        return;
      }

      if (
        nextState !== null &&
        data.length > 2 &&
        zoomEnabled &&
        e.isTrusted &&
        nextState.activeLabel
      ) {
        setRefAreaLeft(nextState.activeLabel);
        e.preventDefault(); // prevents selection of axis's labels while zooming
      }
    },
    [data.length, zoomEnabled, setRefAreaLeft]
  );

  const onMouseMove = useCallback(
    (nextState: MouseHandlerDataParam, e: SyntheticEvent) => {
      // if syncId is present, recharts will trigger this event for all charts
      // so we need to check if the active chart is the one we want to zoom
      if (zoomEnabled && e.isTrusted && refAreaLeft && nextState.activeLabel) {
        setRefAreaRight(nextState.activeLabel);
      }
    },
    [zoomEnabled, refAreaLeft, setRefAreaRight]
  );

  return { onMouseDown, onMouseMove };
};
